import Axios, {AxiosError, AxiosRequestConfig} from 'axios';
import {getAccessToken} from '@/utils/storage';
import pinia from '@/store/index';
import useGlobalStore from '@/store/global-store';
import router from '@/router';
import * as Sentry from '@sentry/vue';

const global = useGlobalStore(pinia);

type ErrorFromServerT = {
    timestamp: string;
    status: number;
    error: string;
    path: string;
    message: string;
};

const baseHttpRequest = Axios.create({
    baseURL:
        process.env.NODE_ENV == 'production'
            ? import.meta.env.VITE_APP_BASE_URL
            : '/api',
    timeout: 40000,
    timeoutErrorMessage:
        '연결이 원활하지 않아요.<br/>잠시후 다시 이용해주세요.',
});

baseHttpRequest.interceptors.request.use(
    async (config: AxiosRequestConfig) => {
        global.setIsLoadingTrue();
        const token = getAccessToken();

        if (token) {
            config.headers!['Authorization'] = `Bearer ${token}`;
        }

        return config;
    },
    (error: AxiosError) => {
        const {method, url, params, data, headers} = error.config;

        Sentry.setContext('API Request Detail', {
            method,
            url,
            params,
            data,
            headers,
        });

        global.setIsLoadingFalse();
        console.error(error);
    },
);

baseHttpRequest.interceptors.response.use(
    (response) => {
        // 응답 후 데이터 캡처
        Sentry.addBreadcrumb({
            category: 'xhr',
            message: 'Axios Interceptor Response',
            data: response.data,
        });

        global.setIsLoadingFalse();

        return response;
    },
    (error: AxiosError<ErrorFromServerT>) => {
        const data = error.response?.data;
        const status = error.response?.status;
        Sentry.captureException(error, {
            level: 'error',
            extra: {
                detail: JSON.stringify(error),
                from: 'src/api/base-http-request.ts',
            },
        });

        Sentry.setContext('API Response Detail', {
            status,
            data,
        });

        global.setIsLoadingFalse();

        if (error.code === 'ECONNABORTED') {
            // NOTE: 타임아웃 에러 시
            Sentry.captureException(error, {
                level: 'error',
                extra: {
                    code: error.code,
                    status: error.response?.data.status,
                    header: error.config.headers,
                    request: error.request,
                    response: error.response?.data,
                    message: error.message,
                    from: 'src/api/base-http-request.ts',
                },
            });

            console.error('[TIMEOUT ERROR]', error);

            return Promise.reject(error);
        }

        error.message =
            error?.response?.data?.message ||
            '불편을 드려 죄송합니다.<br/>일시적인 오류가 발생했습니다.<br/>잠시 후 다시 시도해주세요.';

        if (error?.response?.data?.status) {
            error.status = String(error?.response?.data?.status);
        }

        if (error?.response?.data?.status === 401) {
            // NOTE: 401 에러는 로그인 만료가 유일(백엔드 확인완료 2023-05-16)하므로, interceptor에서 공통 에러처리

            Sentry.captureException(error, {
                level: 'error',
                extra: {
                    code: error.code,
                    status: error.response?.data.status,
                    header: error.config.headers,
                    request: error.request,
                    response: error.response?.data,
                    message: error.message,
                    from: 'src/api/base-http-request.ts',
                },
            });

            window.customAlert(
                error?.response?.data?.message ||
                    '로그인 유효 시간이 만료되었어요.<br/>다시 로그인해주세요.',
                router.replace({name: 'Login'}),
            );
        }

        return Promise.reject(error);
    },
);

export default baseHttpRequest;
